import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTargetRect } from '../hooks/useTargetRect';
import type { TourDefinition } from '../types';
import { TourSpotlight } from './TourSpotlight';
import { TourTooltip } from './TourTooltip';
import { useTour } from '../hooks/useTour';

interface TourOverlayProps {
  tour: TourDefinition;
  stepIndex: number;
}

/**
 * Top-level overlay rendered while a tour is active. Resolves the current
 * step's target, renders the spotlight + tooltip, and forwards control events
 * back to the provider via {@link useTour}.
 *
 * Rendered into a portal so it isn't trapped by ancestor `overflow: hidden`
 * containers.
 */
export function TourOverlay({ tour, stepIndex }: TourOverlayProps) {
  const step = tour.steps[stepIndex];
  const { element, rect } = useTargetRect(step?.target ?? null);
  const { next, prev, skip } = useTour();

  // Scroll the target into view whenever it (or the step) changes. We don't
  // wait on this — the spotlight + tooltip will follow the rect as it moves.
  // Element (not just HTMLElement) supports scrollIntoView, but TS's lib.dom
  // typing can flag it on SVGElement; cast through unknown for safety.
  useEffect(() => {
    if (!element) return;
    type Scrollable = { scrollIntoView: (opts?: ScrollIntoViewOptions) => void };
    const scrollable = element as unknown as Scrollable;
    if (typeof scrollable.scrollIntoView === 'function') {
      scrollable.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
    }
  }, [element]);

  // Close on Escape (and arrow / enter for nav). Only react to real user
  // input — synthetic ``KeyboardEvent``s dispatched by tour ``onBeforeNext``
  // hooks (e.g. to close a Radix Dialog) carry ``isTrusted=false`` and we
  // ignore them so they pass through to the host UI without dismissing the
  // tour as a side effect.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!e.isTrusted) return;
      if (e.key === 'Escape') void skip();
      if (e.key === 'ArrowRight' || e.key === 'Enter') next();
      if (e.key === 'ArrowLeft') prev();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [next, prev, skip]);

  if (!step) return null;
  if (typeof document === 'undefined') return null;

  return createPortal(
    <>
      <TourSpotlight rect={rect} padding={step.spotlightPadding ?? 8} />
      <TourTooltip
        step={step}
        rect={rect}
        tourName={tour.name}
        stepIndex={stepIndex}
        totalSteps={tour.steps.length}
        onNext={next}
        onPrev={prev}
        onSkip={() => void skip()}
      />
    </>,
    document.body
  );
}
